import { AsNumber, AsDatePicker, AsText, AsSelect, isAuthorised } from '@adserve/adserve-react-components';
import React, { useContext, useState, useEffect } from 'react';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import AccountNameCompactTableColumnDefinitions from './ColumnDefinitions/AccountNameCompactTableColumnDefinitions';
import CategoryCompactTableColumnDefinitions from './ColumnDefinitions/CategoryCompactTableColumnDefinitions';
import DemographicCompactTableColumnDefinitions from './ColumnDefinitions/DemographicCompactTableColumnDefinitions';
import AsTextWithStatusAndSearch from '../../generic/AsTextWithStatusAndSearch';
import CompactTable from '../../generic/CompactTable/CompactTableNew';
import {
    labelStyle,
    inputStyle,
    compactTableInputStyle,
    statusInputStyle,
    descriptionInputStyle,
    jcnSurveyContainerStyle,
    fixedInfoTextStyle,
    inputweekStyle
} from './CampaignBriefStyles';
import { AccountsLookup, CategoriesLookup, DemographicsLookup } from './CampaignBriefLookupService';
import HorizontalInfoPipe from './HorizontalInfoPipe/HorizontalInfoPipe';
import AsSelectWithPlaceholder from '../../generic/AsSelectWithPlaceholder';
import { BusinessTypes, CampaignGoalEnum } from '../../../constants/Enums';
import moment from 'moment';
import AsTextarea from '../../generic/AsTextArea';
import { AirtimePlanningContext } from '../../../contexts/AirtimePlanningContext';
import { DefaultInputStatus } from './CampaignBriefUtils';
import { sectionedPanelStyle } from '../CampaignStyles';

const errorStyle = {
    borderColor: '#C40606',
    backgroundColor: '#FFF3F3'
};

const statusLabelStyle = {
    position: 'absolute',
    color: '#C40606',
    fontSize: 12
};

const RequiredBriefDetails = ({
    campaign,
    updateCampaign,
    revenueTypes,
    focusName,
    changeName,
    readOnly = false,
    bookedReadOnly = false,
    ValidationInputCategory,
    errorValidationCategory,
    setErrorValidationCategory,
    errorValidationAccount,
    setErrorValidationAccount,
    validationInput,
    setValidationInput,
    setBillingAccountId,
    setCampaignStartDate,
    setCampaignEndDate,
    campaignNameStatus,
    setCampaignNameStatus,
    surveys,
    deals = [],
    recalculateOffAirs,
    canEditName, // ASSP-7081
    editAuthorisedOnly = false
}) => {
    const { propertyStats, setPropertyStats, showInvalidatePlanMessage } = useContext(AirtimePlanningContext);
    const { dateFormat, formatNumber } = useContext(SystemSettingsContext);
    const [minDateProperty, setMinDateProperty] = useState(null);
    const [maxDateProperty, setMaxDateProperty] = useState(null);
    const [minPropertyStartDate, setMinPropertyStartDate] = useState();
    const [maxPropertyEndDate, setMaxPropertyEndDate] = useState();

    const minDate = moment().utc(true).startOf('day');

    const fromDate = new Date(campaign?.startDate);
    const maxDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());

    const handleChange = (event) => {
        const { name, value } = event.target;
        updateCampaign(name, value);
    };

    const handleWeeksChange = (event) => {
        const numWeeks = event.target.value;

        if (!numWeeks) {
            // Clear end date and weeks if target is empty
            updateCampaign('endDate', null);
            setCampaignEndDate(null);
            updateCampaign('weeks', null);
            return;
        }

        if (numWeeks === campaign.weeks || numWeeks < 1) { 
            return;
        }

        const startDate = new Date(campaign.startDate);
        const lastDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 7 * numWeeks);
        const endDate = new Date(lastDate - 1);

        updateCampaign('endDate', endDate);
        setCampaignEndDate(endDate);
        updateCampaign('weeks', numWeeks);
        invalidateWeeklyBudget();

        showInvalidatePlanMessage(event.target);
    };

    const handleNameChange = (event) => {
        const { name, value } = event.target;
        setCampaignNameStatus(DefaultInputStatus());
        changeName(name, value);
    }; 

    const invalidateWeeklyBudget = () => {
        const selectedGoal = campaign.campaignGoal.find((g) => g.selected === true);
        if (selectedGoal.goal === CampaignGoalEnum.WEEKLYBUDGET) {
            selectedGoal.value = undefined;
            selectedGoal.values = undefined;
            selectedGoal.selected = false;

            const budgetGoal = campaign.campaignGoal.find((g) => g.goal === CampaignGoalEnum.BUDGET);
            budgetGoal.selected = true;
            updateCampaign('campaignGoal', campaign.campaignGoal);
        }
    };

    const handleStartDateChange = (event) => {
        const value = event.target.value;

        if (campaign.endDate && campaign.weeks) {
            // Only set end date if weeks is not null so it isn't treated as 0 weeks
            const endDate = moment(value)
                .add(campaign.weeks * 7 - 1, 'd')
                .toDate();
            updateCampaign('endDate', endDate);
            setCampaignEndDate(endDate);
        }
        
        updateCampaign('startDate', value);
        setCampaignStartDate(value);
        invalidateWeeklyBudget();

        showInvalidatePlanMessage(event.target, false);
    };

    const handleEndDateChange = (event) => {
        const value = event.target.value;
        updateCampaign('endDate', value);
        updateCampaign('weeks', '');
        setCampaignEndDate(value);
        invalidateWeeklyBudget();

        showInvalidatePlanMessage(event.target, false);
    };

    const handleSandpStartDateChange = (event) => {
        const value = moment(event.target.value).utc(true).startOf('day');

        setMinDateProperty(value);
        updateCampaign('startDate', value);
    };

    const handleSandpEndDateChange = (event) => {
        const value = moment(event.target.value).utc(true).startOf('day');
        setMaxDateProperty(value);
        updateCampaign('endDate', value);
    };

    const handleCategoryChange = (event) => {
        updateCampaign('productCategoryName', event.target.value);
        setErrorValidationCategory('');
        setValidationInput(true);
    };

    const handleCategoryDropdownClick = (searchResult) => {
        campaign.productCategoryId = searchResult.id;
        updateCampaign('productCategoryName', searchResult.name);
    };

    const handleDemographicDropdownClick = (searchResult) => {
        campaign.demographicId = searchResult.id;
        updateCampaign('demographicName', searchResult.name);

        showInvalidatePlanMessage(null, false);
    };

    const handleDemographicChange = (event) => {
        const { name, value } = event.target;
        updateCampaign(name, value);

        showInvalidatePlanMessage(event.target, false);
    };

    const handleRevenueTypeChange = (e) => {
        campaign.revenueTypeId = e.target.value;
        updateCampaign('revenueTypeId', e.target.value);
    };

    const handleAdvertiserChange = (event) => {
        updateCampaign('advertiserAccountName', event.target.value);
        updateCampaign('advertiserAccountId', null);
    };

    const handleAdvertiserDropdownClick = (searchResult) => {
        updateCampaign('advertiserAccountId', searchResult.id);
        updateCampaign('advertiserAccountName', searchResult.name);
        setErrorValidationAccount('');
        if (searchResult.productCategoryId !== null) {
            updateCampaign('productCategoryId', searchResult.productCategoryId);
            updateCampaign('productCategoryName', searchResult.productCategoryName);
        }
        if (campaign.agencyAccountId === null) {
            if (campaign.barterAgencyAccountId === null) {
                updateCampaign('billingAccountId', searchResult.id);
                updateCampaign('billingAccountName', searchResult.name);
                setBillingAccountId(searchResult.id);
            }
            updateCampaign('commissionRate', searchResult.commissionRate);
            updateCampaign('vatId', searchResult.vatId);
        }
    };

    const handleAdvertiserRemoveClick = () => {
        updateCampaign('advertiserAccountName', '');
        updateCampaign('advertiserAccountId', null);
        if (campaign.barterAgencyAccountId === null) {
            updateCampaign('billingAccountId', campaign.agencyAccountId);
            updateCampaign('billingAccountName', campaign.agencyAccountName);
            setBillingAccountId(campaign.agencyAccountId);
            if (campaign.agencyAccountId === null) {
                updateCampaign('dealVersionId', window.emptyGuid);
            }
        }
    };

    const handleAgencyDropdownClick = (searchResult) => {
        updateCampaign('agencyAccountId', searchResult.id);
        updateCampaign('agencyAccountName', searchResult.name);
        setErrorValidationAccount('');
        if (campaign.barterAgencyAccountId === null) {
            updateCampaign('billingAccountId', searchResult.id);
            updateCampaign('billingAccountName', searchResult.name);
            updateCampaign('commissionRate', searchResult.commissionRate);
            updateCampaign('vatId', searchResult.vatId);
            setBillingAccountId(searchResult.id);
        }
    };

    const handleAgencyChange = (event) => {
        updateCampaign('agencyAccountName', event.target.value);
        updateCampaign('agencyAccountId', null);
    };

    const handleAgencyRemoveClick = () => {
        updateCampaign('agencyAccountName', '');
        updateCampaign('agencyAccountId', null);
        if (campaign.barterAgencyAccountId === null) {
            updateCampaign('billingAccountId', campaign.advertiserAccountId);
            updateCampaign('billingAccountName', campaign.advertiserAccountName);
            updateCampaign('commissionRate', 0);
            setBillingAccountId(campaign.advertiserAccountId);
            if (campaign.advertiserAccountId === null) {
                updateCampaign('dealVersionId', window.emptyGuid);
            }
        }
    };

    const findMinPropertyDate = () => {
        const dates = [];
        propertyStats?.forEach((b) => {
            if (b !== undefined && new Date(b['startDate']).valueOf() !== 0) {
                dates.push(moment(b['startDate']).utc(true).startOf('day'));
            }
        });
        if (!dates.length) {
            setMinPropertyStartDate(undefined);
            return;
        }

        setMinPropertyStartDate(dates.length > 0 ? moment.min(dates).utc(true).startOf('day') : undefined);

        if (campaign.startDate) {
            dates.push(moment(campaign.startDate).utc(true).startOf('day'));
        }
        if (minDateProperty) {
            dates.push(minDateProperty);
        }

        const minDate = dates.length > 0 ? moment.min(dates).utc(true).startOf('day') : undefined;
        return minDate;
    };

    const findMaxPropertyDate = () => {
        const dates = [];
        propertyStats?.forEach((b) => {
            if (b !== undefined && new Date(b['endDate']).valueOf() !== 0) {
                dates.push(moment(b['endDate']).utc(true).startOf('day'));
            }
        });
        if (!dates.length) {
            setMaxPropertyEndDate(undefined);
            return;
        }

        setMaxPropertyEndDate(dates.length > 0 ? moment.max(dates).utc(true).startOf('day') : undefined);

        if (campaign.endDate) {
            dates.push(moment(campaign.endDate).utc(true).startOf('day'));
        }
        if (maxDateProperty) {
            dates.push(maxDateProperty);
        }

        const maxDate = dates.length > 0 ? moment.max(dates).utc(true).startOf('day') : undefined;
        return maxDate;
    };

    useEffect(() => {
        if (campaign?.campaignTypeName?.toLowerCase() === 'sandp') {
            const min = findMinPropertyDate();

            if (min) {
                if (min?.unix() !== minDateProperty?.unix()) {
                    setMinDateProperty(min);
                }

                if (min.unix() !== moment(campaign?.startDate).utc(true).startOf('day').unix()) {
                    updateCampaign('startDate', min, false);
                }
            }

            const max = findMaxPropertyDate();
            if (max) {
                if (max?.unix() !== maxDateProperty?.unix()) {
                    setMaxDateProperty(max);
                }
                if (max.unix() !== moment(campaign?.endDate).utc(true).startOf('day').unix()) {
                    updateCampaign('endDate', max, false);
                }
            }
        }
    }, [propertyStats, campaign.campaignTypeName]);

    useEffect(() => {
        if (campaign && campaign.startDate && campaign.endDate) {
            const startDate = moment(campaign.startDate).utc(true).startOf('day');
            const endDate = moment(campaign.endDate).utc(true).startOf('day');
            const diffInDays = endDate.diff(startDate, 'day');
            const weeks = Math.floor(diffInDays / 7) + 1;
            if (typeof campaign === 'string') {
                campaign = JSON.parse(campaign);
            }
            campaign.weeks = weeks;

            setCampaignStartDate(campaign.startDate);
            setCampaignEndDate(campaign.endDate);
            if (campaign?.campaignTypeName?.toLowerCase() === 'sandp' && propertyStats?.length) {
                setMinDateProperty(startDate);
                setMaxDateProperty(endDate);
            }
        }
    }, [campaign]);

    useEffect(() => {
        if (!readOnly) campaign.stationsSelected = false;
    }, [readOnly]);

    const isRunningCampaign = () => {
        return campaign.versionNumber > 0 && new Date(Date.parse(campaign?.startDate)) <= new Date();
    };

    const isRunningOrBooked = () => {
        return readOnly || bookedReadOnly || campaign?.campaignVersions?.length > 1 || isRunningCampaign();
    };

    return (
        <div
            style={{
                ...sectionedPanelStyle,
                padding: '10px 230px',
                display: 'grid',
                gap: '0 80px',
                boxSizing: 'border-box',
                width: '100%',
                gridTemplateColumns: 'repeat(4, 1fr)'
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', gridColumn: '1 / -1', paddingBottom: 10 }}>
                <span style={{ fontSize: 15, fontWeight: 500 }}>{window.translate('Campaign Brief *')}</span>
            </div>
            <AsTextWithStatusAndSearch
                id='campaign-name'
                name='name'
                title={window.translate('Campaign name')}
                labelStyle={labelStyle}
                inputStyle={statusInputStyle}
                value={campaign.name}
                // ASSP-7081 From campaign-advertiser etc - enable for all statuses if edit button is clicked
                disabled={!isAuthorised('EDIT_BOOKED_CAM') === false ? !canEditName : isRunningOrBooked()} // will not update 
                // disabled={readOnly} // ASSP-7081 Original; editable until booked
                onChange={handleNameChange}
                onFocus={() => focusName(true)}
                searchStatus={campaignNameStatus}
                numResultsInDropdown={0}
            />
            <AsSelect
                id='campaign-revenue-type'
                name='revenueTypeId'
                labelStyle={labelStyle}
                selectStyle={inputStyle}
                values={revenueTypes}
                value={
                    campaign.revenueTypeId ??
                    revenueTypes.find((rt) => rt.defaultForBusinessTypeName === campaign.businessType)
                }
                onChange={handleRevenueTypeChange}
                disabled={readOnly || isRunningCampaign()}
                title={window.translate('Revenue Type')}
            />

            <div>
                <label style={labelStyle}>{window.translate('Advertiser')}</label>
                <CompactTable
                    id='campaign-advertiser'
                    inputStyle={{
                        ...compactTableInputStyle,
                        ...(errorValidationAccount ? errorStyle : { borderColor: '#D7D7D7' })
                    }}
                    value={campaign.advertiserAccountName}
                    onChange={handleAdvertiserChange}
                    searchFunction={(searchTerm, params) => AccountsLookup(searchTerm, params, 'advertiser')}
                    tableColumnDefinitions={AccountNameCompactTableColumnDefinitions}
                    onDropdownClick={handleAdvertiserDropdownClick}
                    disabled={!isAuthorised('EDIT_BOOKED_CAM') === false ? !editAuthorisedOnly : isRunningOrBooked()}
                    errorName='Advertiser'
                    showClearButton={!!campaign.advertiserAccountName && !isRunningOrBooked()}
                    onRemoveClick={handleAdvertiserRemoveClick}
                />
            </div>
            <div>
                <label style={labelStyle}>{window.translate('Agency')}</label>
                <CompactTable
                    id='campaign-agency'
                    inputStyle={{
                        ...compactTableInputStyle,
                        ...(errorValidationAccount ? errorStyle : { borderColor: '#D7D7D7' })
                    }}
                    value={campaign.agencyAccountName}
                    onChange={handleAgencyChange}
                    searchFunction={(searchTerm, params) => AccountsLookup(searchTerm, params, 'agency')}
                    tableColumnDefinitions={AccountNameCompactTableColumnDefinitions}
                    onDropdownClick={handleAgencyDropdownClick}
                    disabled={!isAuthorised('EDIT_BOOKED_CAM') === false ? !editAuthorisedOnly : isRunningOrBooked()}
                    errorName='Agency'
                    statusStyle={{ width: 280, textAlign: 'end' }}
                    showClearButton={!!campaign.agencyAccountName && !isRunningOrBooked()}
                    onRemoveClick={handleAgencyRemoveClick}
                />
            </div>
            <div style={{ gridColumn: '1 / 3' }} />
            <div style={{ gridColumn: '-3 / -1', display: 'flex', justifyContent: 'center' }}>
                <HorizontalInfoPipe
                    height={11}
                    width={256}
                    text={window.translate('One required')}
                    hasError={errorValidationAccount}
                />
            </div>

            {campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase() ? (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
                        <div>
                            <label style={labelStyle}>{window.translate('Campaign start date')}</label>
                            <AsDatePicker
                                id='start-date'
                                name='startDate'
                                labelStyle={labelStyle}
                                inputStyle={inputStyle}
                                currentDate={campaign?.startDate}
                                placeholder=''
                                onChange={handleSandpStartDateChange}
                                disabled={readOnly || isRunningCampaign()}
                                dateFormat={dateFormat}
                                containerStyle={{ width: '100%' }}
                                style={{ flexWrap: 'no-wrap' }}
                                buttonStyle={{ width: 56 }}
                                maxDate={minPropertyStartDate ?? maxDateProperty ?? undefined}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>{window.translate('Campaign end date')}</label>
                            <AsDatePicker
                                id='end-date'
                                name='endDate'
                                labelStyle={labelStyle}
                                inputStyle={inputStyle}
                                currentDate={campaign?.endDate}
                                placeholder=''
                                onChange={handleSandpEndDateChange}
                                disabled={!isAuthorised('EDIT_BOOKED_CAM') === false ? !editAuthorisedOnly : readOnly}
                                dateFormat={dateFormat}
                                containerStyle={{ width: '100%' }}
                                style={{ flexWrap: 'no-wrap' }}
                                buttonStyle={{ width: 56 }}
                                minDate={maxPropertyEndDate ?? minDateProperty}
                            />
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div
                        style={{
                            display: 'grid',
                            gap: 14,
                            gridTemplateColumns: '1fr max-content 1fr',
                            gridColumn: 'span 2',
                            width: '100%'
                        }}
                    >
                        <div>
                            <label style={labelStyle}>{window.translate('Campaign start date')}</label>
                            <AsDatePicker
                                id='campaign-startdate'
                                inputStyle={inputStyle}
                                currentDate={campaign.startDate || 'Select date ...'}
                                onChange={handleStartDateChange}
                                minDate={
                                    campaign?.businessType?.toLowerCase() === BusinessTypes.AIRTIME.name.toLowerCase()
                                        ? minDate
                                        : moment().utc(true).startOf('day').add('days', -1)
                                }
                                handleMultiple={true}
                                disabled={readOnly || isRunningCampaign()}
                                dateFormat={dateFormat}
                                containerStyle={{ width: '100%' }}
                                style={{ flexWrap: 'no-wrap' }}
                                buttonStyle={{ width: 56 }}
                            />
                        </div>
                        {campaign?.businessType && (
                            <div>
                                <AsNumber
                                    id='campaign-week-input'
                                    name='weeks'
                                    title={window.translate('# of weeks')}
                                    labelStyle={labelStyle}
                                    inputStyle={inputweekStyle}
                                    value={campaign.weeks}
                                    onChange={handleWeeksChange}
                                    disabled={readOnly}
                                    allowFloat={false}
                                    formatNumber={formatNumber}
                                    digitLimit={5}
                                />
                            </div>
                        )}
                        <div>
                            <label style={labelStyle}>{window.translate('Campaign end date')}</label>
                            <AsDatePicker
                                id='campaign-enddate'
                                inputStyle={inputStyle}
                                currentDate={campaign.endDate || 'Select date ...'}
                                onChange={handleEndDateChange}
                                minDate={maxDate}
                                handleMultiple={true}
                                disabled={readOnly}
                                dateFormat={dateFormat}
                                containerStyle={{ width: '100%' }}
                                style={{ flexWrap: 'no-wrap' }}
                                buttonStyle={{ width: 56 }}
                            />
                        </div>
                    </div>
                </>
            )}

            {campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase() && (
                <AsSelectWithPlaceholder
                    id='campaign-deal-name'
                    name='dealVersionId'
                    inputStyle={{ fontSize: 15, fontWeight: 600, padding: '0 12px', height: 40, width: '100%' }}
                    placeholderText='No active deals are found'
                    title={window.translate('Deal name')}
                    values={deals}
                    value={campaign?.dealVersionId !== window.emptyGuid ? campaign?.dealVersionId : 0}
                    disabled={true}
                />
            )}

            {campaign?.businessType?.toLowerCase() === BusinessTypes.OFFAIRS.name.toLowerCase() ||
            campaign?.businessType?.toLowerCase() === BusinessTypes.DIGITAL.name.toLowerCase() ? (
                <div>&nbsp;</div>
            ) : (
                <div>
                    <label style={labelStyle}>{window.translate('Target Demographic')}</label>
                    <CompactTable
                        id='campaign-target-demographic'
                        name='demographicId'
                        minSearchLength={0}
                        inputStyle={compactTableInputStyle}
                        value={campaign.demographicName || 0}
                        onChange={handleDemographicChange}
                        searchFunction={(searchTerm, params) => DemographicsLookup(searchTerm, params)}
                        tableColumnDefinitions={DemographicCompactTableColumnDefinitions}
                        onDropdownClick={handleDemographicDropdownClick}
                        disabled={readOnly || isRunningCampaign()}
                    />
                </div>
            )}

            {campaign?.businessType?.toLowerCase() === BusinessTypes.OFFAIRS.name.toLowerCase() ||
            campaign?.businessType?.toLowerCase() === BusinessTypes.DIGITAL.name.toLowerCase() ? (
                <div>&nbsp;</div>
            ) : (
                <div>
                    <label style={labelStyle}>{window.translate('Category')}</label>
                    <CompactTable
                        id='campaign-category'
                        name='productCategoryId'
                        minSearchLength={0}
                        inputStyle={{
                            ...compactTableInputStyle,
                            ...(ValidationInputCategory !== true && validationInput !== true
                                ? errorStyle
                                : { borderColor: '#D7D7D7' })
                        }}
                        value={campaign.productCategoryName}
                        onChange={handleCategoryChange}
                        searchFunction={(searchTerm, params) => CategoriesLookup(searchTerm, params)}
                        tableColumnDefinitions={CategoryCompactTableColumnDefinitions}
                        onDropdownClick={handleCategoryDropdownClick}
                        disabled={!isAuthorised('EDIT_BOOKED_CAM') === false ? !editAuthorisedOnly : readOnly}
                    />
                    {ValidationInputCategory !== true && (
                        <label style={statusLabelStyle}>{errorValidationCategory}</label>
                    )}
                </div>
            )}
            <div style={{ width: '100%', gridColumn: '1 / -1', paddingTop: 5 }}>
                <AsTextarea
                    id='campaign-description'
                    name='description'
                    title={window.translate('Description')}
                    labelStyle={labelStyle}
                    inputStyle={descriptionInputStyle}
                    value={campaign.description}
                    onChange={handleChange}
                    disabled={bookedReadOnly}
                    maxLength={200}
                />
            </div>
            <div style={{ ...jcnSurveyContainerStyle, gridColumn: '1 / -1' }}>
                <div
                    style={{
                        width: '100%',
                        paddingTop: 13,
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}
                >
                    <div id={'campaign-jcn-number'}>
                        <span style={fixedInfoTextStyle}>
                            {window.translate('JCN Number: ')}({campaign.jcn ? campaign.jcn : window.translate('None')})
                        </span>
                    </div>
                    <div id={'campaign-survey-input'}>
                        <span style={fixedInfoTextStyle}>
                            {window.translate('Audience Survey')}
                            {': '}
                            {campaign?.surveyId ? surveys.filter((x) => x.id === campaign.surveyId)[0]?.name : ''}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RequiredBriefDetails;
